<h2>{{ paymentOptions?.title }}</h2>
<mat-divider style="margin-bottom: 30px"></mat-divider>

@if (isFetchingData && !errorMessage) {
  <div fxLayout="column" fxLayoutAlign="center center">
    <mat-spinner strokeWidth="2" diameter="50"></mat-spinner>
    <p>Loading...</p>
  </div>
}
@if (errorMessage) {
  <div class="mb-1">
    {{ errorMessage }}
  </div>
}

<div
  [formGroup]="form"
  [ngClass]="
    isFetchingData || paymentOptions?.isCheckoutAvailable === false
      ? 'd-none'
      : ''
  "
>
  <!--header stuff-->
  <div [innerHtml]="paymentOptions?.description | safeHtml"></div>
  <div class="mb-1" fxLayout="column">
    @if (paymentOptions?.options?.length > 1) {
      <mat-form-field appearance="outline">
        <mat-label for="nameOnCard">Payment Type</mat-label>
        <mat-select [required-aria]="true" [(value)]="selectedPaymentOption">
          @for (option of paymentOptions.options; track option) {
            <mat-option [value]="option">
              {{ option.description }}
            </mat-option>
          }
        </mat-select>
      </mat-form-field>
    }
    @if (selectedPaymentOption) {
      <table mat-table [dataSource]="selectedPaymentOption.lineItems">
        <ng-container matColumnDef="label">
          <th mat-header-cell *matHeaderCellDef>Fee</th>
          <th mat-footer-cell *matFooterCellDef>Total</th>
          <td mat-cell *matCellDef="let element" class="custom-fields-name">
            {{ element.label }}
          </td>
        </ng-container>
        <ng-container matColumnDef="amount">
          <th mat-header-cell *matHeaderCellDef>Amount</th>
          <td mat-cell *matCellDef="let element" class="custom-fields-name">
            {{ element.amount | currency: 'USD' }}
          </td>
          <th mat-footer-cell *matFooterCellDef>
            {{ selectedPaymentOption.totalAmount | currency: 'USD' }}
          </th>
        </ng-container>
        <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-footer-row *matFooterRowDef="displayedColumns"></tr>
      </table>
    }
  </div>

  @if (selectedPaymentOption?.paymentType !== 'Check') {
    <img
      class="ml-1 mb-1"
      alt="Payments powered by Stripe. Accepted cards: Visa, Mastercard, American Express, and Discover"
      src="/assets/images/powered-by-stripe.png"
    />
  }

  <!--CARD STUFF-->
  <div
    class="two-columns"
    fxLayout.xs="column"
    [ngClass]="{ 'd-none': selectedPaymentOption?.paymentType !== 'Card' }"
  >
    <!--
    We do some hacks below to get Stripe Elements to play nicely with Angular Material. The actual input controlled by Angular Material is hidden.
    It's just there to satisfy mat-form-field's requirements. The actual inputs come from Stripe Elements.
    -->
    <div fxLayout="column">
      <label for="nameOnCard"
        >Name on Card <span class="required">*</span></label
      >
      <mat-form-field appearance="outline">
        <input
          id="nameOnCard"
          matInput
          formControlName="nameOnCard"
          required
          placeholder="Name on Card"
        />
        @if (form.controls.nameOnCard.hasError('required')) {
          <mat-error>Name on Card is required</mat-error>
        }
      </mat-form-field>
    </div>
    <div fxLayout="column">
      <label for="card-expiry"
        >Expiration Date <span class="required">*</span></label
      >
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <div id="card-expiry"></div>
        <input
          formControlName="hiddenField"
          [errorStateMatcher]="expiryError"
          [attr.aria-describedby]="
            stripeFieldErrors.expiry ? cardExpiryErrorMessageId : null
          "
          matInput
          class="d-none"
        />
        @if (stripeFieldErrors.expiry) {
          <mat-error
            [id]="cardExpiryErrorMessageId"
            aria-live="polite"
            role="alert"
          >
            {{ stripeFieldErrors.expiry }}
          </mat-error>
        }
      </mat-form-field>
    </div>
    <div fxLayout="column">
      <label for="card-number"
        >Card Number <span class="required">*</span></label
      >
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <div id="card-number"></div>
        <input
          formControlName="hiddenField"
          [errorStateMatcher]="cardNumberError"
          [attr.aria-describedby]="
            stripeFieldErrors.cardNumber ? cardNumberErrorMessageId : null
          "
          matInput
          class="d-none"
        />
        @if (stripeFieldErrors.cardNumber) {
          <mat-error
            [id]="cardNumberErrorMessageId"
            aria-live="polite"
            role="alert"
          >
            {{ stripeFieldErrors.cardNumber }}
          </mat-error>
        }
      </mat-form-field>
    </div>
    <div fxLayout="column">
      <label for="card-cvc"
        >Security Code <span class="required">*</span></label
      >
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <div id="card-cvc"></div>
        <input
          formControlName="hiddenField"
          [errorStateMatcher]="cvcError"
          [attr.aria-describedby]="
            stripeFieldErrors.cvc ? cardCvcErrorMessageId : null
          "
          matInput
          class="d-none"
        />
        @if (stripeFieldErrors.cvc) {
          <mat-error
            [id]="cardCvcErrorMessageId"
            aria-live="polite"
            role="alert"
          >
            {{ stripeFieldErrors.cvc }}
          </mat-error>
        }
      </mat-form-field>
    </div>
  </div>
  @if (selectedPaymentOption?.paymentType === 'Card') {
    <div class="mb-1">
      @if (addressFromApplication) {
        <mat-checkbox color="primary" formControlName="isUsingAddressProvided"
          >Use the address I provided</mat-checkbox
        >
      }
      <h3>Billing Address</h3>
      @if (addressFromApplication && form.value.isUsingAddressProvided) {
        <read-only-address
          [address]="addressFromApplication"
          [hideStatus]="true"
        >
          <div class="semi-bold"></div>
        </read-only-address>
      }
      @if (!form.value.isUsingAddressProvided) {
        <address-editor [address]="manualMailingAddress"></address-editor>
      }
    </div>
  }
  <!--ACH Stuff-->
  @if (selectedPaymentOption?.paymentType === 'Ach') {
    <div class="mb-1">
      <div fxLayout="column">
        <label for="nameOnCard"
          >Name on Account <span class="required">*</span></label
        >
        <mat-form-field appearance="outline">
          <input
            id="nameOnCard"
            matInput
            formControlName="nameOnAccount"
            required
            placeholder="Name on Account"
          />
        </mat-form-field>
      </div>
      @if (!achPaymentMethod?.id) {
        <button
          mat-flat-button
          color="primary"
          (click)="connectACH()"
          type="button"
        >
          Search for your bank<mat-icon>launch</mat-icon>
        </button>
      }
      @if (achPaymentMethod?.id) {
        <div fxLayout="row" fxLayoutAlign="space-between center">
          <div fxLayout="row">
            <mat-icon class="mr-medium">account_balance</mat-icon
            >{{ achPaymentMethod.us_bank_account.bank_name }}
            {{ achPaymentMethod.us_bank_account.account_type }} (XXX{{
              achPaymentMethod.us_bank_account.last4
            }})
          </div>
          <button mat-icon-button type="button" (click)="removeACH()">
            <mat-icon>cancel</mat-icon>
          </button>
        </div>
      }
    </div>
  }
  @if (selectedPaymentOption?.paymentType === 'Check') {
    <div
      class="mb-1"
      fxLayout="column"
      [innerHtml]="paymentOptions?.checkInstructions | safeHtml"
    ></div>
  }
</div>

@if (paymentOptions?.isCheckoutAvailable === false) {
  <div class="mb-1">
    The total amount due is $0. Click submit to finish your application.
  </div>
}
